/**
 * FamiliarisationGuideModal - Help and guidance for the familiarisation system
 */

import React from 'react';
import { X, BookOpen, CheckCircle, AlertTriangle, Clock, FileText } from 'lucide-react';

const FamiliarisationGuideModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
        
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center">
              <BookOpen className="h-6 w-6 text-cyan-600 mr-3" />
              <h2 className="text-xl font-semibold text-gray-900">Familiarisation Guide</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Overview */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Overview</h3>
              <p className="text-gray-600">
                The vessel familiarisation system ensures all crew members understand vessel-specific 
                systems, procedures, and safety requirements before assuming their duties.
              </p>
            </div>

            {/* Requirements */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Key Requirements</h3>
              <div className="space-y-3">
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">Complete all mandatory sections</p>
                    <p className="text-sm text-gray-600">Based on crew role and vessel configuration</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">Task-specific training</p>
                    <p className="text-sm text-gray-600">Sections adapt based on assigned duties</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <AlertTriangle className="h-5 w-5 text-amber-500 mr-3 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">Renewal requirements</p>
                    <p className="text-sm text-gray-600">
                      Check your SMS policy for renewal frequency (typically when changing vessels or roles)
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <FileText className="h-5 w-5 text-blue-500 mr-3 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">Digital signatures required</p>
                    <p className="text-sm text-gray-600">Both crew member and assessor must sign off</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Features */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">System Features</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-gray-400 rounded-full mr-3"></span>
                  Customizable checklists - Add company-specific requirements
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-gray-400 rounded-full mr-3"></span>
                  Paper mode available - For existing paper-based systems
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-gray-400 rounded-full mr-3"></span>
                  Progress tracking - Save and resume familiarisation
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-gray-400 rounded-full mr-3"></span>
                  Export certificates - PDF records for compliance
                </li>
              </ul>
            </div>

            {/* Regulatory Reference */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-1">Regulatory Reference</h4>
              <p className="text-sm text-blue-800">
                UK Workboat Code Edition 3, Appendix 8, Sections 7.1 and 7.2
              </p>
              <p className="text-xs text-blue-700 mt-2">
                Section 7.1: Task-specific familiarisation requirements<br />
                Section 7.2: Minimum familiarisation requirements
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-lg">
            <button
              onClick={onClose}
              className="w-full sm:w-auto px-4 py-2 bg-cyan-600 text-white text-sm font-medium rounded-md hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
            >
              Close Guide
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FamiliarisationGuideModal;